import React, { useState, useEffect, useCallback } from 'react';
import { Search, RefreshCw, Check } from 'lucide-react';
import {
    idibuApi,
    type IdibuPortal,
    type PortalCategory,
    type PortalCountry,
} from '../services/idibu.api';
import PortalLogo from './PortalLogo';

const PAGE_SIZE = 20;

interface BoardMarketplaceProps {
    onBoardClick: (portal: IdibuPortal) => void;
}

export default function BoardMarketplace({ onBoardClick }: BoardMarketplaceProps) {
    const [portals, setPortals] = useState<IdibuPortal[]>([]);
    const [subscribedIds, setSubscribedIds] = useState<Set<number>>(new Set());
    const [subscribedPortals, setSubscribedPortals] = useState<IdibuPortal[]>([]);
    const [categories, setCategories] = useState<PortalCategory[]>([]);
    const [countries, setCountries] = useState<PortalCountry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [search, setSearch] = useState('');
    const [filterCountry, setFilterCountry] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const loadFilters = useCallback(async () => {
        try {
            const [catRes, countryRes] = await Promise.all([
                idibuApi.getPortalCategories(),
                idibuApi.getPortalCountries(),
            ]);
            setCategories(catRes.data.categories || []);
            setCountries(countryRes.data.countries || []);
        } catch {
            /* non-critical */
        }
    }, []);

    const loadSubscribed = useCallback(async () => {
        try {
            const { data } = await idibuApi.listSubscribedPortals();
            const list = data.portals || [];
            setSubscribedPortals(list);
            setSubscribedIds(new Set(list.map((p: IdibuPortal) => Number(p.id))));
        } catch {
            /* non-critical */
        }
    }, []);

    const loadPortals = useCallback(
        async (newOffset: number, append = false) => {
            setLoading(true);
            setError(null);
            try {
                const params: Record<string, string | number> = {
                    count: PAGE_SIZE,
                    offset: newOffset,
                };
                if (search.trim()) params.filter_name = search.trim();
                if (filterCountry) params.filter_country = filterCountry;
                if (filterCategory) params.filter_tags = filterCategory;

                const { data } = await idibuApi.listAllPortals(params);
                const newPortals = data.portals || [];
                setPortals((prev) => (append ? [...prev, ...newPortals] : newPortals));
                setHasMore(newPortals.length >= PAGE_SIZE);
                setOffset(newOffset);
            } catch (err: any) {
                setError(err?.response?.data?.error || err.message || 'Failed to load boards');
            } finally {
                setLoading(false);
            }
        },
        [search, filterCountry, filterCategory],
    );

    useEffect(() => {
        loadFilters();
        loadSubscribed();
    }, [loadFilters, loadSubscribed]);

    useEffect(() => {
        setOffset(0);
        loadPortals(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, filterCountry, filterCategory]);

    const handleLoadMore = () => loadPortals(offset + PAGE_SIZE, true);

    const refreshSubscribed = useCallback(async () => {
        await loadSubscribed();
    }, [loadSubscribed]);

    // Keep subscribed boards pinned at the top while preserving server order
    // (which, in demo mode, means popularity ranking).
    const mergedPortals = React.useMemo(() => {
        const allIds = new Set(portals.map((p) => Number(p.id)));
        const missingSubscribed = subscribedPortals.filter((p) => !allIds.has(Number(p.id)));
        const combined = [...missingSubscribed, ...portals];
        return combined.slice().sort((a, b) => {
            const aSub = subscribedIds.has(Number(a.id)) ? 0 : 1;
            const bSub = subscribedIds.has(Number(b.id)) ? 0 : 1;
            return aSub - bSub;
        });
    }, [portals, subscribedPortals, subscribedIds]);

    return (
        <>
            <style>{MARKETPLACE_CSS}</style>
            <section className="bm">
                <header className="bm__header">
                    <div>
                        <h3>Job boards</h3>
                        <p>Browse available job boards and connect the ones you need.</p>
                    </div>
                    <button
                        type="button"
                        className="bm__refresh"
                        onClick={refreshSubscribed}
                        title="Refresh connected status"
                        aria-label="Refresh"
                    >
                        <RefreshCw size={14} />
                    </button>
                </header>

                <div className="bm__filters">
                    <label className="bm__search">
                        <Search size={14} aria-hidden />
                        <input
                            type="text"
                            placeholder="Search boards…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            aria-label="Search boards"
                        />
                    </label>
                    <select value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)} aria-label="Filter by country">
                        <option value="">All countries</option>
                        {countries.map((c) => (
                            <option key={c.code} value={c.code}>{c.name}</option>
                        ))}
                    </select>
                    <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} aria-label="Filter by category">
                        <option value="">All categories</option>
                        {categories.map((c, i) => (
                            <option key={`${c.category}-${i}`} value={c.category}>{c.category}</option>
                        ))}
                    </select>
                </div>

                {error && (
                    <div className="bm__error">
                        <span>{error}</span>
                        <button type="button" onClick={() => setError(null)} aria-label="Dismiss">×</button>
                    </div>
                )}

                {!loading && mergedPortals.length === 0 && (
                    <div className="bm__empty">No boards match your filters.</div>
                )}

                <div className="bm__grid">
                    {mergedPortals.map((portal) => (
                        <PortalCard
                            key={portal.id}
                            portal={portal}
                            isSubscribed={subscribedIds.has(Number(portal.id))}
                            onClick={() => onBoardClick(portal)}
                        />
                    ))}
                </div>

                {loading && <div className="bm__loading">Loading boards…</div>}

                {!loading && hasMore && portals.length > 0 && (
                    <div className="bm__more">
                        <button type="button" className="bm__more-btn" onClick={handleLoadMore}>
                            Load more
                        </button>
                    </div>
                )}
            </section>
        </>
    );
}

export { type BoardMarketplaceProps };

// ─── Portal card ─────────────────────────────────────────────────────────────

function PortalCard({
    portal,
    isSubscribed,
    onClick,
}: {
    portal: IdibuPortal;
    isSubscribed: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`bm-card ${isSubscribed ? 'bm-card--subscribed' : ''}`}
        >
            <PortalLogo logo={portal.logo} name={portal.name} size={44} imageSize={32} />
            <span className="bm-card__body">
                <span className="bm-card__title-row">
                    <span className="bm-card__title">{portal.name}</span>
                    {isSubscribed && (
                        <span className="bm-card__badge">
                            <Check size={11} strokeWidth={3} /> Connected
                        </span>
                    )}
                </span>
                <span className="bm-card__meta">
                    {portal.country ? <span>{portal.country}</span> : null}
                    {portal.country && portal.description ? <span aria-hidden> · </span> : null}
                    {portal.description && (
                        <span className="bm-card__desc">
                            {portal.description.length > 64
                                ? portal.description.slice(0, 64) + '…'
                                : portal.description}
                        </span>
                    )}
                </span>
            </span>
        </button>
    );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const MARKETPLACE_CSS = `
.bm {
    margin-top: 8px;
}

.bm__header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
}

.bm__header h3 {
    margin: 0;
    color: var(--text-primary);
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.005em;
}

.bm__header p {
    margin: 3px 0 0;
    color: var(--text-tertiary);
    font-size: 12.5px;
}

.bm__refresh {
    display: grid;
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    place-items: center;
    border: 1px solid var(--border-default);
    border-radius: var(--radius-md);
    background: var(--surface-primary);
    color: var(--text-secondary);
    cursor: pointer;
    transition: background 100ms ease, color 100ms ease;
}

.bm__refresh:hover {
    background: var(--surface-hover);
    color: var(--text-primary);
}

.bm__filters {
    display: grid;
    grid-template-columns: minmax(200px, 1fr) auto auto;
    gap: 8px;
    margin-bottom: 14px;
}

.bm__search {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    height: 34px;
    padding: 0 12px;
    border: 1px solid var(--border-default);
    border-radius: var(--radius-md);
    background: var(--surface-primary);
    color: var(--text-tertiary);
    transition: border-color 100ms ease, box-shadow 100ms ease;
}

.bm__search:focus-within {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-subtle);
    color: var(--text-secondary);
}

.bm__search input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    color: var(--text-primary);
    font-size: 13px;
    font-family: inherit;
}

.bm__search input::placeholder {
    color: var(--text-tertiary);
}

.bm__filters select {
    height: 34px;
    padding: 0 10px;
    border: 1px solid var(--border-default);
    border-radius: var(--radius-md);
    background: var(--surface-primary);
    color: var(--text-secondary);
    font-family: inherit;
    font-size: 13px;
    outline: none;
    cursor: pointer;
    transition: border-color 100ms ease;
}

.bm__filters select:hover {
    border-color: var(--border-emphasis);
    color: var(--text-primary);
}

.bm__filters select:focus {
    border-color: var(--accent);
}

.bm__error {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
    padding: 10px 14px;
    border-radius: var(--radius-md);
    background: var(--bg-danger);
    color: var(--fg-danger);
    border: 1px solid rgba(207, 34, 46, 0.18);
    font-size: 13px;
}

.bm__error button {
    color: inherit;
    font-size: 16px;
    line-height: 1;
}

.bm__empty {
    padding: 32px;
    text-align: center;
    color: var(--text-tertiary);
    font-size: 13px;
}

.bm__loading {
    padding: 24px;
    text-align: center;
    color: var(--text-tertiary);
    font-size: 13px;
}

.bm__more {
    display: flex;
    justify-content: center;
    margin-top: 16px;
}

.bm__more-btn {
    height: 32px;
    padding: 0 18px;
    border: 1px solid var(--border-default);
    border-radius: var(--radius-md);
    background: var(--surface-primary);
    color: var(--text-secondary);
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: background 100ms ease, color 100ms ease;
}

.bm__more-btn:hover {
    background: var(--surface-hover);
    color: var(--text-primary);
}

.bm__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 10px;
}

.bm-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border: 1px solid var(--border-default);
    border-radius: var(--radius-md);
    background: var(--surface-primary);
    text-align: left;
    cursor: pointer;
    transition: background 100ms ease, border-color 100ms ease, box-shadow 100ms ease;
    width: 100%;
}

.bm-card:hover {
    border-color: var(--border-emphasis);
    background: var(--surface-hover);
}

.bm-card--subscribed {
    border-color: rgba(26, 127, 55, 0.30);
    background: linear-gradient(180deg, rgba(218, 251, 225, 0.45), var(--surface-primary));
}

.bm-card--subscribed:hover {
    border-color: rgba(26, 127, 55, 0.55);
    background: var(--bg-success);
}

.bm-card__body {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.bm-card__title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.bm-card__title {
    color: var(--text-primary);
    font-size: 13.5px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.bm-card__badge {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    flex-shrink: 0;
    padding: 1px 6px 1px 4px;
    border-radius: 999px;
    background: var(--bg-success);
    color: var(--fg-success);
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.02em;
}

.bm-card__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    color: var(--text-tertiary);
    font-size: 12px;
    line-height: 1.4;
    overflow: hidden;
}

.bm-card__desc {
    color: var(--text-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
`;
